/**
 * User-facing console logging utilities that only log in browser environments.
 *
 * These functions check for the presence of `window` before logging to avoid
 * polluting server-side logs (e.g., Fly.io logs) with warnings/errors that
 * are only relevant to end users in the browser.
 *
 * @see REPL-21800
 */

type ConsoleLogType = "warn" | "error";

const REPLO_PREFIX = "[Replo]";

/**
 * Logs a message to the console only if running in a browser environment.
 * This prevents noisy server-side logs while still surfacing helpful
 * messages to users in the browser.
 *
 * @param type - The type of console log
 * @param args - Arguments to pass to console.warn or console.error
 */
function userFacingConsoleLog(type: ConsoleLogType, ...args: unknown[]) {
  if (typeof window === "undefined") {
    return;
  }

  if (type === "warn") {
    console.warn(REPLO_PREFIX, ...args);
  } else {
    console.error(REPLO_PREFIX, ...args);
  }
}

/**
 * Convenience wrapper for userFacingConsoleLog("warn", ...)
 */
export function userFacingConsoleWarn(...args: unknown[]) {
  userFacingConsoleLog("warn", ...args);
}

/**
 * Convenience wrapper for userFacingConsoleLog("error", ...)
 */
export function userFacingConsoleError(...args: unknown[]) {
  userFacingConsoleLog("error", ...args);
}
